import { Buffer } from 'buffer';
import { timelockEncrypt } from 'ts-ibe';

// Base URL for the chain API that exposes the IBE public key.
// Can be overridden via REACT_APP_CHAIN_API_URL; defaults to local node.
const CHAIN_API_BASE_URL =
  process.env.REACT_APP_CHAIN_API_URL || 'https://testnet-api.fairblock.network';

// Cache the active IBE public key so we only hit the chain API once per session.
let cachedIbePublicKey = null;
let cachedIbePublicKeyPromise = null;

/**
 * Fetch the active IBE public key from the chain API.
 * Expected endpoint: `${CHAIN_API_BASE_URL}/fairyring/keyshare/pubkey`
 * with response body containing: { active_pubkey: { public_key: "..." }, ... }
 */
async function getActiveIbePublicKey() {
  if (cachedIbePublicKey) {
    return cachedIbePublicKey;
  }

  if (cachedIbePublicKeyPromise) {
    return cachedIbePublicKeyPromise;
  }

  const baseUrl = CHAIN_API_BASE_URL.replace(/\/$/, '');
  const url = `${baseUrl}/fairyring/keyshare/pubkey`;

  console.log('[IBE] Fetching active public key from:', url);

  cachedIbePublicKeyPromise = (async () => {
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      cachedIbePublicKeyPromise = null;
      throw new Error(
        `[IBE] Failed to fetch active public key from ${url}: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    const publicKey =
      data?.active_pubkey?.public_key || data?.active_pubkey?.pubkey;

    if (!publicKey) {
      cachedIbePublicKeyPromise = null;
      throw new Error(
        '[IBE] Active public key not found in chain API response'
      );
    }

    cachedIbePublicKey = publicKey;
    console.log('[IBE] Active public key:', cachedIbePublicKey);
    return cachedIbePublicKey;
  })();

  return cachedIbePublicKeyPromise;
}

/**
 * Encrypts transfer amount using IBE (Identity-Based Encryption)
 * @param {string} userAddress - User's wallet address (used as the IBE ID)
 * @param {string} chainKey - Chain identifier (deprecated, not used in ID)
 * @param {string|number} amount - Transfer amount to encrypt
 * @returns {Promise<string>} Base64-encoded encrypted amount
 */
export const encryptTransferAmount = async (userAddress, chainKey, amount) => {
  // Fetch active IBE public key from chain API
  const ibePublicKey = await getActiveIbePublicKey();

  // Create ID using only user address
  const id = userAddress.toLowerCase();

  // Convert amount to Buffer
  // Amount can be a string or number, convert to string first, then to Buffer
  const amountString = typeof amount === 'number' ? amount.toString() : String(amount);
  const amountBuffer = Buffer.from(amountString, 'utf8');

  // Encrypt using IBE
  // timelockEncrypt returns a Promise<string> (already encoded)
  const encrypted = await timelockEncrypt(id, ibePublicKey, amountBuffer);

  // Return the encrypted string for storage in database
  return encrypted;
};

/**
 * Encrypts randomness (PedersenOpening) using IBE (Identity-Based Encryption)
 * @param {string} userAddress - User's wallet address (used as the IBE ID)
 * @param {string} chainKey - Chain identifier (deprecated, not used in ID)
 * @param {string} randomnessBase64 - Base64-encoded randomness (PedersenOpening)
 * @returns {Promise<string>} Base64-encoded encrypted randomness
 */
export const encryptRandomness = async (userAddress, chainKey, randomnessBase64) => {
  // Fetch active IBE public key from chain API
  const ibePublicKey = await getActiveIbePublicKey();

  // Create ID using only user address
  const id = userAddress.toLowerCase();

  // Log ID for testing purposes
  console.log('[IBE Encryption] ID used for randomness encryption:', id);
  console.log('[IBE Encryption] User address:', userAddress.toLowerCase());

  // Convert base64 randomness to Buffer
  const randomnessBuffer = Buffer.from(randomnessBase64, 'base64');

  // Encrypt using IBE
  // timelockEncrypt returns a Promise<string> (already encoded)
  const encrypted = await timelockEncrypt(id, ibePublicKey, randomnessBuffer);

  // Return the encrypted string for storage in database
  return encrypted;
};

